// PURPOSE: Make every table row open its record when clicked anywhere, not just on the name link.
// CONTRACT: the row's first in-app link is the target; clicks on buttons, inputs, other links or selected text are left alone; ctrl/cmd/middle-click opens a new tab.
// DEPENDS: React Router navigate. CEILING: presentation only; the link inside the row stays the accessible control.
import { useEffect } from 'react';
import type { NavigateFunction } from 'react-router-dom';

const INTERACTIVE = 'a, button, input, select, textarea, label, summary, details, form, [role="button"], [role="menuitem"]';

export function rowTarget(row: Element): HTMLAnchorElement | null {
  return row.querySelector<HTMLAnchorElement>('a[href^="/"]');
}

export function useClickableRows(navigate: NavigateFunction) {
  useEffect(() => {
    const open = (event: MouseEvent) => {
      if (event.defaultPrevented || (event.button !== 0 && event.button !== 1)) return;
      const target = event.target as Element | null;
      const row = target?.closest('table tbody tr, .cb-card, .outreach-card, .outreach-list-row');
      if (!row || target?.closest(INTERACTIVE)) return;
      if (window.getSelection()?.toString()) return;
      const link = rowTarget(row);
      if (!link) return;
      const href = link.getAttribute('href')!;
      if (event.button === 1 || event.ctrlKey || event.metaKey) window.open(href, '_blank', 'noopener');
      else navigate(href);
    };
    document.addEventListener('click', open);
    document.addEventListener('auxclick', open);
    return () => { document.removeEventListener('click', open); document.removeEventListener('auxclick', open); };
  }, [navigate]);
}
